import { useState, useEffect, useRef, ChangeEvent } from 'react'
import MainLayout from '../../components/MainLayout'
import colors from '../../constants/colors'
import CardTAG from '../../components/CardTAG'
import FormLayout from '../../components/forms/FormLayout'
import DividerTAG from '../../components/DividerTAG'
import LabelErrorTAG from '../../components/LabelErrorTAG'
import FilterComponent, { IFilterObject, IPreset } from '../../components/FilterComponent'
import { updateFilterObjectCheckedValue } from '../ExportPage'
import useDebounce from '../../hooks/useDebounce'
import ModalTAG from '../../components/ModalTAG'
import reportsService from '../../services/reportsService'
import usersService from '../../services/usersService'
import { IAuthJWT } from '../../config/axios'
import DateRangePicker from '../../components/DateRangePicker'
import { selectStyle } from '../../constants/styleConstants'
import {
  Drawer,
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Stack,
} from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import jwtDecode from 'jwt-decode'
import dayjs from 'dayjs'

import AddIcon from '@mui/icons-material/Add'
import { ReactComponent as DownIcon } from '../../assets/images/DownIcon.svg'
import { ReactComponent as ArrowIcon } from '../../assets/images/arrowIcon.svg'
import constants from './constants'
import { IGroup } from './types'
import utils from './utils'
import html2canvas from 'html2canvas'
import addPersonFormUtils from '../../components/forms/AddPersonForm/utils'
import { useComponentVisible } from '../../components/IconDropdown'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../store'
import { getUserDetails } from '../../store/auth/authSlice'
import { isEmptyObj } from '../../utils'
import useAutoLogout from '../../hooks/useAutoLogout'

const currentTime = dayjs()

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)
export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top' as const,
    },
  },
  datasets: {
    bar: {
      barPercentage: 0.5,
      categoryPercentage: 0.6,
      maxBarThickness: 120,
    },
  },
}

const renderSelectOptions = (options: { title: string; value: string }[]) => {
  return options.map(key => {
    return (
      <MenuItem value={key.value} key={key.value}>
        <Typography variant='bodyBold'>{key.title}</Typography>
      </MenuItem>
    )
  })
}
const defaultGroupId = utils.getRandomInt()
const MetricsPage: React.FC = () => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(true)
  const [xAxisOption, setXAxisOptions] = useState<string>('month')
  const [graphTitle, setGraphTitle] = useState<string>('Graph Title')
  const [filter, setFilter] = useState<IFilterObject[]>(constants.defaultFilter)
  const debouncedFilterValue = useDebounce(filter, 500)
  const [presets, setPresets] = useState<IPreset[]>([])
  const [openAddGroupModal, setOpenAddGroupModal] = useState(false)
  const [newGroupName, setNewGroupName] = useState<string>('')
  const [chartData, setChartData] = useState<any>(undefined)
  const [groups, setGroups] = useState<IGroup[]>([
    { groupName: 'Group 1', filter: constants.defaultFilter, id: defaultGroupId },
  ])
  const autoLogout = useAutoLogout()
  const [dateRange, setDateRange] = useState<(Date | null)[] | undefined>([
    dayjs().startOf('year').toDate(),
    currentTime.toDate(),
  ])
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const [rStartDate, rEndDate] = dateRange
  const [openRenameGroupModal, setOpenRenameGroupModal] = useState<boolean>(false)
  const [groupToRename, setGroupToRename] = useState<number>()
  const [openBackgroundFilter, setOpenBackgroundFilter] = useState<boolean>(false)
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false)

  const onChangeHandler = (event: SelectChangeEvent) => {
    setXAxisOptions(event?.target?.value)
  }
  const titleChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGraphTitle(event?.target?.value)
  }
  const groupNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewGroupName(event?.target?.value)
  }
  const onChangeFilterHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const response = updateFilterObjectCheckedValue(filter, [event?.target?.value])
    if (response) {
      setFilter(response)
    }
  }
  const onChangePresetHandler = (event: React.ChangeEvent<HTMLInputElement>, groupId: number) => {
    const clickedId = Number(event?.target?.value)
    const clickedPreset = presets.find(p => p.id === clickedId)
    const isChecked = clickedPreset ? !clickedPreset.checked : true
    setPresets(presets.map(p =>
      p.id === clickedId ? { ...p, checked: isChecked } : { ...p, checked: false }
    ))
    const newFilter = isChecked && clickedPreset
      ? updateFilterObjectCheckedValue(constants.defaultFilter, clickedPreset.values) ?? constants.defaultFilter
      : constants.defaultFilter
    setGroups(prev => prev.map(group =>
      group.id === groupId ? { ...group, filter: newFilter } : group
    ))
  }

  const rangeClickHandler = () => {
    setOpenBackgroundFilter(true)
  }

  const closeRangeHandler = () => {
    setOpenBackgroundFilter(false)
  }

  const addGroup = () => {
    setGroups(prev => [
      ...prev,
      { groupName: newGroupName, filter: constants.defaultFilter, id: utils.getRandomInt() },
    ])
    setNewGroupName('')
    setOpenAddGroupModal(false)
  }

  const saveGroupAsPreset = async (groupName: string, groupId: number) => {
    const tokenData: IAuthJWT = jwtDecode(localStorage.getItem('accessToken') || '')
    const groupFilter = groups.find(g => g.id === groupId)?.filter ?? debouncedFilterValue
    const selectedValues = {
      ...utils.getSelectedValuesForPreset(groupFilter),
      name: groupName,
      user: tokenData.user_id,
    }
    const res = await usersService.postUserPreset(selectedValues)
    if (res.status >= 200 && res.status <= 400) getPresets()
  }

  function getMonthName(monthNumber: string) {
    const date = dayjs().month(+monthNumber - 1)

    // Using the browser's default locale.
    return date.format('MMMM')
  }

  const transformMonthNumbersToNames = (data: any) => {
    return data?.map((monthNumber: string) => {
      if (monthNumber === 'all') {
        return 'All'
      }
      return getMonthName(monthNumber)
    })
  }

  const getMonthKeysForRange = (startDate: Date, endDate: Date) => {
    const start = dayjs(startDate).startOf('month')
    const end = dayjs(endDate).startOf('month')
    const keys: string[] = []

    let current = start
    while (current.isBefore(end) || current.isSame(end)) {
      keys.push(String(current.month() + 1))
      current = current.add(1, 'month')
    }

    return keys
  }

  const getYearKeysForRange = (startDate: Date, endDate: Date) => {
    const startYear = dayjs(startDate).year()
    const endYear = dayjs(endDate).year()
    const keys: string[] = []

    for (let year = startYear; year <= endYear; year += 1) {
      keys.push(String(year))
    }

    return keys
  }

  const doFiltering = async (filter: any) => {
    const { data } = await reportsService.getMetricsFiltered(filter)
    if (!isEmptyObj(data)) {
      const dataKeys = Object.keys(data)
      const xAxisKeys =
        xAxisOption === 'month' && rStartDate && rEndDate
          ? getMonthKeysForRange(rStartDate, rEndDate)
          : xAxisOption === 'year' && rStartDate && rEndDate
            ? getYearKeysForRange(rStartDate, rEndDate)
          : xAxisOption === 'month'
            ? dataKeys.filter(key => key !== 'all')
            : xAxisOption === 'year'
              ? dataKeys.filter(key => key !== 'all')
            : dataKeys

      const labels = xAxisOption === 'month' ? transformMonthNumbersToNames(xAxisKeys) : xAxisKeys

      const allGroupsIds = data['all']?.map(
        (groupInfo: { group_id: number }) => groupInfo?.group_id,
      )

      const datasets = allGroupsIds?.map((group: number, index: number) => {
        const group1 = groups.find(existingGroup => existingGroup.id == group)
        const count = xAxisKeys?.map(key => {
          const found = data[key]?.find((info: { group_id: any }) => info.group_id === group)

          if (found) {
            return found?.count
          }
          return 0
        })

        return {
          data: count,
          backgroundColor: constants.chartColors[index],
          borderRadius: 30,
          label: group1?.groupName ?? 'default name',
        }
      })

      const newChartData = {
        labels,
        datasets,
      }
      setChartData(newChartData)
    } else setChartData(null)
  }

  const getPresets = async () => {
    const tokenData: IAuthJWT = jwtDecode(localStorage.getItem('accessToken') || '')
    const { data } = await usersService.getUserPresets(tokenData?.user_id)
    setPresets(utils.formatPresets(data))
  }

  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    getPresets()
    const tokenData: IAuthJWT = jwtDecode(localStorage.getItem('accessToken') || '')
    dispatch(getUserDetails({ userId: tokenData.user_id }))
  }, [])

  useEffect(() => {
    const groupsDataForFilter = groups?.map(group => ({
      ...utils.getSelectedValues(group?.filter),
      id: group?.id,
      group_by: xAxisOption,
      start_date: rStartDate,
      end_date: rEndDate,
    }))
    const dataForFilter = [
      ...groupsDataForFilter,
      {
        ...utils.getSelectedValues(filter),
        id: 1,
        group_by: xAxisOption,
        start_date: rStartDate,
        end_date: rEndDate,
      },
    ]
    rEndDate && rStartDate && doFiltering([...groupsDataForFilter])
  }, [filter, groups, xAxisOption, rStartDate, rEndDate])

  const removeGroup = (groupId: number) => {
    setGroups(prev => prev.filter(group => group.id !== groupId))
  }

  const renameGroup = () => {
    setOpenRenameGroupModal(false)
    setGroups(prev =>
      prev?.map(group => {
        if (group?.id === groupToRename) {
          return {
            ...group,
            groupName: newGroupName,
          }
        }
        return group
      }),
    )
    setNewGroupName('')
  }

  const onChangeGroupHandler = (event: ChangeEvent<HTMLInputElement>, groupId: number) => {
    const updatedGroups = groups.map(group => {
      if (group?.id === groupId) {
        const response = updateFilterObjectCheckedValue(group.filter, [event?.target?.value])
        if (response) {
          return {
            ...group,
            filter: response,
          }
        }
      }
      return group
    })
    setGroups(updatedGroups)
  }

  const renderButtons = (groupId: number, groupName: string) => {
    return (
      <Stack direction='row' gap={1}>
        <Button
          variant='outlined'
          onClick={() => {
            saveGroupAsPreset(groupName, groupId)
          }}
        >
          <Typography variant='textLarge' color='inherit'>
            Save Group As Preset
          </Typography>
        </Button>
        <Button
          variant='outlined'
          onClick={() => {
            setGroupToRename(groupId)
            setNewGroupName(groupName)
            setOpenRenameGroupModal(true)
          }}
        >
          <Typography variant='textLarge' color='inherit'>
            Rename Group
          </Typography>
        </Button>
        {defaultGroupId !== groupId && (
          <Button variant='outlined' onClick={() => removeGroup(groupId)}>
            <Typography variant='textLarge' color='inherit'>
              Remove Group
            </Typography>
          </Button>
        )}
      </Stack>
    )
  }

  const renderGroupsDropdowns = (groups: IGroup[]) => {
    return groups.map(({ id, groupName, filter }) => {
      const selectedCount = utils.getSelectedValuesCount(filter)

      return (
        <FormLayout key={id}>
          <Box sx={{ display: 'flex', flexDirection: 'column ', gap: '.4rem' }}>
            <LabelErrorTAG label={groupName} />
            <Select
              onChange={e => onChangeHandler(e)}
              IconComponent={ArrowIcon}
              displayEmpty
              value={''}
              renderValue={selected => {
                if (selected.length === 0) {
                  return <Typography>({selectedCount}) Selected</Typography>
                }

                return ''
              }}
              sx={[selectStyle]}
              MenuProps={{
                PaperProps: {
                  sx: {
                    display: 'inline-block',
                    minWidth: '40vw !important',
                    maxWidth: 'initial',
                    overflow: 'visible',
                    backgroundColor: 'white',
                  },
                },
              }}
            >
              <FilterComponent
                filterObject={filter}
                onChangeHandler={e => onChangeGroupHandler(e, id)}
                presets={presets}
                onChangePresetHandler={e => onChangePresetHandler(e, id)}
              />
            </Select>
          </Box>
          {renderButtons(id, groupName)}
          <DividerTAG />
        </FormLayout>
      )
    })
  }

  const exportRef = useRef()

  const downloadHandler = async () => {
    if (exportRef && exportRef.current) {
      const canvas = await html2canvas(exportRef.current)
      const image = canvas.toDataURL('image/png', 1.0)
      downloadImage(image, addPersonFormUtils.camalize(graphTitle) ?? 'Graph')
    }
  }

  const downloadImage = (blob: string, fileName: string) => {
    const fakeLink = window.document.createElement('a')
    fakeLink.download = fileName
    fakeLink.href = blob
    document.body.appendChild(fakeLink)
    fakeLink.click()
    document.body.removeChild(fakeLink)
    fakeLink.remove()
  }

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
            flexGrow: 1,
          }}
        >
          <Drawer
            sx={{
              ...(openDrawer ? { width: '35vw' } : { display: 'none' }),
              flexShrink: 0,
              overflowX: 'hidden',
              position: 'relative',
              '& .MuiDrawer-paper': {
                width: '35vw',
                boxSizing: 'border-box',
                position: 'unset',
              },
            }}
            variant='persistent'
            anchor='left'
            open={openDrawer}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: colors.WHITE,
                height: '100%',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: colors.TAG_LIGHT_BLUE,
                  padding: '2rem 3.6rem',
                }}
              >
                <Typography variant='h3' color={colors.TAG_BLUE}>
                  {' '}
                  Company Metrics{' '}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '4rem 3.7rem',
                  backgroundColor: colors.WHITE,
                  height: '100%',
                }}
              >
                <FormLayout>
                  {groups.length > 0 && renderGroupsDropdowns(groups)}

                  <Button
                    variant='outlined'
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setOpenAddGroupModal(true)
                    }}
                  >
                    <Typography variant='bodyRegular' color='inherit'>
                      Add Group
                    </Typography>
                  </Button>
                  <DividerTAG />
                  <Box sx={{ display: 'flex', flexDirection: 'column ', gap: '.4rem' }}>
                    <LabelErrorTAG label='X Axis' />
                    <Select
                      onChange={onChangeHandler}
                      IconComponent={ArrowIcon}
                      displayEmpty
                      value={xAxisOption}
                      sx={[selectStyle]}
                    >
                      {renderSelectOptions(constants.xAxisOptions)}
                    </Select>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column ', gap: '.4rem' }}>
                    <LabelErrorTAG label='Date Range' />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <DateRangePicker
                        inputClickHandler={rangeClickHandler}
                        closeHandler={closeRangeHandler}
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        rangePicker
                        ref={ref}
                      />
                    </Box>
                  </Box>
                  <DividerTAG />
                  <Box sx={{ display: 'flex', flexDirection: 'column ', gap: '.4rem' }}>
                    <LabelErrorTAG label='Title' />
                    <TextField
                      variant='standard'
                      id='username-input'
                      aria-describedby='username-helper-text'
                      fullWidth
                      value={graphTitle}
                      onChange={titleChangeHandler}
                      sx={{
                        backgroundColor: colors.WHITE,
                        border: `1px solid ${colors.TAG_LINES_GRAY}`,
                        borderRadius: '.5rem',
                      }}
                    />
                  </Box>
                  <Button
                    startIcon={<DownIcon fill={colors.TAG_BLUE} />}
                    variant='submitFormButton'
                    onClick={downloadHandler}
                  >
                    {' '}
                    <Typography variant='bodyBold'>Save Image</Typography>
                  </Button>
                </FormLayout>
              </Box>
            </Box>
          </Drawer>
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              width: '100vw',
              ...(openDrawer && {
                width: 'calc(100% - 35vw)',
              }),
            }}
          >
            <Button
              onClick={() => {
                setOpenDrawer(!openDrawer)
              }}
              sx={{
                borderRight: `1px solid ${colors.TAG_BORDER_BLUE}`,
                borderLeft: `1px solid ${colors.TAG_BORDER_BLUE}`,
                borderRadius: 'unset',
                backgroundColor: colors.TAG_LIGHT_BLUE,
                minWidth: 'unset',
                p: '1.5rem',
              }}
            >
              <Stack sx={{ width: '0px' }}>
                <Typography
                  sx={{ transform: 'rotate(-90deg)', '-webkit-transform': 'rotate(-90deg)' }}
                >
                  {openDrawer ? 'Minimize' : 'Expand'}
                </Typography>
              </Stack>
            </Button>
            <Box
              sx={{ maxHeight: '70vh', padding: '3.6rem', width: '100%' }}
              id='canvas-to-save'
              ref={exportRef}
            >
              <CardTAG title={graphTitle}>
                <Box sx={{ width: '100%', maxHeight: '70vh' }}>
                  {chartData ? (
                    <Bar options={options} data={chartData} />
                  ) : (
                    <Typography>There is nothing to show</Typography>
                  )}
                </Box>
              </CardTAG>
            </Box>
          </Box>
        </Box>
      </MainLayout>
      <ModalTAG open={openAddGroupModal}>
        <CardTAG
          title='Add Group'
          closable={true}
          closeCard={() => {
            setOpenAddGroupModal(false)
          }}
        >
          <FormLayout>
            <Box>
              <LabelErrorTAG label='Group Name' />
              <TextField
                variant='standard'
                id='username-input'
                aria-describedby='username-helper-text'
                fullWidth
                value={newGroupName}
                onChange={groupNameChangeHandler}
                sx={{
                  backgroundColor: colors.WHITE,
                  border: `1px solid ${colors.TAG_LINES_GRAY}`,
                  borderRadius: '.5rem',
                }}
              />
            </Box>
            <Button variant='outlined' disabled={newGroupName.length === 0} onClick={addGroup}>
              {' '}
              <Typography variant='bodyRegular' color='inherit'>
                Add
              </Typography>
            </Button>
          </FormLayout>
        </CardTAG>
      </ModalTAG>
      <ModalTAG open={openRenameGroupModal}>
        <CardTAG
          title='Rename Group'
          closable={true}
          closeCard={() => {
            setOpenRenameGroupModal(false)
          }}
        >
          <FormLayout>
            <Box>
              <LabelErrorTAG label='Group Name' />
              <TextField
                variant='standard'
                id='username-input'
                aria-describedby='username-helper-text'
                fullWidth
                value={newGroupName}
                onChange={groupNameChangeHandler}
                sx={{
                  backgroundColor: colors.WHITE,
                  border: `1px solid ${colors.TAG_LINES_GRAY}`,
                  borderRadius: '.5rem',
                }}
              />
            </Box>
            <Button variant='outlined' disabled={newGroupName.length === 0} onClick={renameGroup}>
              {' '}
              <Typography variant='bodyRegular' color='inherit'>
                Rename
              </Typography>
            </Button>
          </FormLayout>
        </CardTAG>
      </ModalTAG>
    </>
  )
}

export default MetricsPage
